import React, { useEffect, useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiErrorDetail, friendlyError } from "../lib/api";
import { ArrowLeft, ArrowRight, Plane, Music, Tent, Bus, Car, Users, MapPin, Calendar, Heart, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { code: "zajezd", label: "Zájezd", emoji: "🚌", Icon: Bus, helper: "Bus, ubytko, parta" },
  { code: "koncert", label: "Koncert", emoji: "🎵", Icon: Music, helper: "Vstupenka + sraz" },
  { code: "festival", label: "Festival", emoji: "🏕️", Icon: Tent, helper: "Více dní, hudba" },
  { code: "sraz", label: "Sraz", emoji: "🍻", Icon: Users, helper: "Casual setkání" },
  { code: "letenky", label: "Letenky + Hotel", emoji: "✈️", Icon: Plane, helper: "Letíme spolu" },
  { code: "vylet", label: "Výlet", emoji: "🚗", Icon: Car, helper: "Auto, příroda" },
];

const COUNTRIES = [
  { code: "CZ", flag: "🇨🇿", name: "Česko" },
  { code: "SK", flag: "🇸🇰", name: "Slovensko" },
  { code: "FR", flag: "🇫🇷", name: "Francie" },
  { code: "GR", flag: "🇬🇷", name: "Řecko" },
  { code: "IT", flag: "🇮🇹", name: "Itálie" },
  { code: "ES", flag: "🇪🇸", name: "Španělsko" },
  { code: "PT", flag: "🇵🇹", name: "Portugalsko" },
  { code: "HR", flag: "🇭🇷", name: "Chorvatsko" },
  { code: "AT", flag: "🇦🇹", name: "Rakousko" },
  { code: "DE", flag: "🇩🇪", name: "Německo" },
  { code: "NL", flag: "🇳🇱", name: "Nizozemsko" },
  { code: "GB", flag: "🇬🇧", name: "Velká Británie" },
  { code: "PL", flag: "🇵🇱", name: "Polsko" },
  { code: "HU", flag: "🇭🇺", name: "Maďarsko" },
  { code: "TR", flag: "🇹🇷", name: "Turecko" },
  { code: "US", flag: "🇺🇸", name: "USA" },
  { code: "TH", flag: "🇹🇭", name: "Thajsko" },
  { code: "MX", flag: "🇲🇽", name: "Mexiko" },
  { code: "MA", flag: "🇲🇦", name: "Maroko" },
  { code: "EG", flag: "🇪🇬", name: "Egypt" },
  { code: "JP", flag: "🇯🇵", name: "Japonsko" },
  { code: "OTHER", flag: "🌍", name: "Jiná země…" },
];

const REGIONS = [
  { code: "praha", name: "Praha" },
  { code: "stredocesky", name: "Středočeský" },
  { code: "jihocesky", name: "Jihočeský" },
  { code: "plzensky", name: "Plzeňský" },
  { code: "karlovarsky", name: "Karlovarský" },
  { code: "ustecky", name: "Ústecký" },
  { code: "liberecky", name: "Liberecký" },
  { code: "kralovehradecky", name: "Královéhradecký" },
  { code: "pardubicky", name: "Pardubický" },
  { code: "vysocina", name: "Vysočina" },
  { code: "jihomoravsky", name: "Jihomoravský" },
  { code: "olomoucky", name: "Olomoucký" },
  { code: "zlinsky", name: "Zlínský" },
  { code: "moravskoslezsky", name: "Moravskoslezský" },
];

const INITIAL = {
  category: "",
  title: "",
  description: "",
  country: "",
  country_custom: "",
  country_note: "",
  city: "",
  region: "",
  date_text: "",
  capacity: 10,
  age_min: "",
  age_max: "",
  price_czk: 0,
  perks: "",
  organizer_note: "",
  truth_consent: false,
};

const DRAFT_KEY = "cy_event_draft_v1";

function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && parsed.form) return parsed;
    return null;
  } catch { return null; }
}

function saveDraft(form, step) {
  try {
    // Skip if entirely empty
    const hasAny = Object.entries(form).some(([k, v]) => {
      if (k === "truth_consent") return false;
      if (k === "capacity" && v === 10) return false;
      return v !== "" && v !== null && v !== undefined;
    });
    if (!hasAny) return;
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, step, savedAt: new Date().toISOString() }));
  } catch { /* ignore */ }
}

function clearDraft() {
  try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
}

export default function AddEventPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [doneId, setDoneId] = useState(null);
  const [draftPrompt, setDraftPrompt] = useState(null); // { form, step, savedAt } or null

  // On mount, check for saved draft
  useEffect(() => {
    const d = loadDraft();
    if (d) setDraftPrompt(d);
  }, []);

  // Auto-save on every change
  useEffect(() => {
    saveDraft(form, step);
  }, [form, step]);

  if (loading) return <div className="min-h-screen bg-paper grid place-items-center text-slate-500">Načítám…</div>;
  if (!user) {
    // Save current form before redirecting to login so it's not lost
    return <Navigate to="/login" replace />;
  }
  if (!user.is_verified) return <Navigate to="/verify" replace />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Build steps array - dynamically include region step only if CZ, custom country only if OTHER
  const showRegion = form.country === "CZ";
  const showCustomCountry = form.country === "OTHER";
  const baseSteps = [
    "category", "title", "description", "country",
    ...(showCustomCountry ? ["country_custom"] : []),
    "country_note", "city",
    ...(showRegion ? ["region"] : []),
    "date_text", "capacity", "age_range", "price", "perks", "organizer_note", "confirm",
  ];

  const current = baseSteps[step];
  const total = baseSteps.length;

  const canNext = () => {
    switch (current) {
      case "category": return !!form.category;
      case "title": return form.title.trim().length >= 2;
      case "description": return form.description.trim().length >= 5;
      case "country": return !!form.country;
      case "country_custom": return form.country_custom.trim().length >= 2;
      case "country_note": return true;
      case "city": return form.city.trim().length >= 1;
      case "region": return !!form.region;
      case "date_text": return form.date_text.trim().length >= 3;
      case "capacity": return form.capacity >= 2 && form.capacity <= 10000;
      case "age_range": return true; // optional
      case "price": return form.price_czk >= 0 && form.price_czk <= 500;
      case "perks": return true;
      case "organizer_note": return form.organizer_note.trim().length >= 5;
      case "confirm": return form.truth_consent;
      default: return true;
    }
  };

  const goNext = () => setStep((s) => Math.min(s + 1, total - 1));
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/events", {
        category: form.category,
        title: form.title.trim(),
        description: form.description.trim(),
        country: form.country,
        country_custom: form.country_custom.trim(),
        country_note: form.country_note.trim(),
        city: form.city.trim(),
        region: showRegion ? form.region : "",
        date_text: form.date_text.trim(),
        capacity: parseInt(form.capacity, 10),
        age_min: parseInt(form.age_min, 10) || 0,
        age_max: parseInt(form.age_max, 10) || 0,
        price_czk: parseInt(form.price_czk, 10) || 0,
        perks: form.perks.trim(),
        organizer_note: form.organizer_note.trim(),
        truth_consent: true,
      });
      setDoneId(data.id);
      toast.success("Akce odeslána! Brzy ji schválíme.");
    } catch (e) {
      if (e.response?.status === 401) {
        setError("Tvoje přihlášení vypršelo. Otevři tuhle stránku v nové záložce: " + window.location.origin + "/login a přihlas se znovu. Tvůj rozepsaný formulář zůstal — nahoře dej zpět.");
      } else {
        setError(friendlyError(e));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // SUCCESS SCREEN
  if (doneId) {
    return (
      <div className="min-h-screen sunset-bg grid place-items-center px-4">
        <div className="bg-white/95 backdrop-blur rounded-3xl border border-white/60 p-8 sm:p-10 max-w-md w-full shadow-2xl text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 grid place-items-center">
            <CheckCircle2 className="w-9 h-9 text-emerald-600" />
          </div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 mt-5">Hotovo! 🎉</h1>
          <p className="text-slate-600 font-medium mt-2">Tvoji akci si přečteme do 24 hodin. Pak ji všichni uvidí.</p>
          <p className="text-slate-500 text-sm mt-3">Děláme to proto, aby tu byly jen pravé akce a žádné podvody.</p>
          <button
            data-testid="addevent-done-home-btn"
            onClick={() => navigate("/")}
            className="mt-7 w-full px-6 py-3.5 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-bold transition-all active:scale-95"
          >
            Zpátky na hlavní stránku
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen sunset-bg">
      <style>{`
        .sunset-bg {
          background: linear-gradient(180deg, #FFF7ED 0%, #FFEDD5 30%, #FECACA 60%, #F7F8F6 100%);
          background-attachment: fixed;
        }
      `}</style>

      {/* HEADER */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-white/70 border-b border-white/40">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <button
            data-testid="addevent-back-btn"
            onClick={step === 0 ? () => navigate("/") : goBack}
            className="w-9 h-9 rounded-full bg-white border border-slate-200 grid place-items-center hover:border-sky-300 active:scale-95"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 h-1.5 rounded-full bg-slate-200/70 overflow-hidden">
            <div
              data-testid="addevent-progress"
              className="h-full bg-sky-500 transition-all duration-300 rounded-full"
              style={{ width: `${((step + 1) / total) * 100}%` }}
            />
          </div>
          <span className="text-xs font-bold text-slate-600 tabular-nums">
            {step + 1}/{total}
          </span>
        </div>
      </header>

      {/* DRAFT RESTORE BANNER */}
      {draftPrompt && (
        <div data-testid="addevent-draft-banner" className="max-w-xl mx-auto px-4 sm:px-6 pt-4">
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1">
              <p className="font-bold text-amber-900 text-sm">Máš rozepsanou akci ✏️</p>
              <p className="text-xs text-amber-700 font-medium mt-0.5">
                Uloženo {new Date(draftPrompt.savedAt).toLocaleString("cs-CZ", { dateStyle: "short", timeStyle: "short" })}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                data-testid="addevent-draft-continue"
                onClick={() => {
                  setForm({ ...INITIAL, ...draftPrompt.form, truth_consent: false });
                  setStep(draftPrompt.step || 0);
                  setDraftPrompt(null);
                }}
                className="px-4 py-2 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold active:scale-95"
              >
                Pokračovat
              </button>
              <button
                data-testid="addevent-draft-discard"
                onClick={() => { clearDraft(); setDraftPrompt(null); }}
                className="px-4 py-2 rounded-full bg-white border border-amber-300 text-amber-700 text-sm font-bold hover:bg-amber-100 active:scale-95"
              >
                Zahodit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MAIN */}
      <main className="max-w-xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-32">
        <div key={current} className="animate-fade-up">
          {current === "category" && (
            <Step title="Co se chystá?">
              <div className="grid grid-cols-2 gap-3" data-testid="addevent-category-grid">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    data-testid={`addevent-category-${c.code}`}
                    onClick={() => { set("category", c.code); setTimeout(goNext, 120); }}
                    className={[
                      "p-5 rounded-2xl bg-white border-2 text-left transition-all active:scale-[0.98] hover:shadow-md",
                      form.category === c.code ? "border-sky-500 shadow-md" : "border-transparent hover:border-sky-200",
                    ].join(" ")}
                  >
                    <div className="text-3xl">{c.emoji}</div>
                    <div className="mt-2 font-heading font-extrabold text-slate-900">{c.label}</div>
                    <div className="text-xs text-slate-500 font-medium">{c.helper}</div>
                  </button>
                ))}
              </div>
            </Step>
          )}

          {current === "title" && (
            <Step title="Jak se akce jmenuje?" helper={`Krátký a jasný název. Třeba: „Víkend v Praze" nebo „Koncert v Brně".`}>
              <input
                autoFocus
                data-testid="addevent-title-input"
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
                maxLength={120}
                placeholder="Víkend v Praze"
                className="input-soft"
              />
            </Step>
          )}

          {current === "description" && (
            <Step title="Napiš jednou větou, o co jde" helper={`Lidé to čtou jako první. Třeba: „Jdeme spolu na koncert a pak na pivo."`}>
              <textarea
                autoFocus
                data-testid="addevent-description-input"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                maxLength={400}
                rows={3}
                placeholder="Jdeme spolu na koncert a pak na pivo."
                className="input-soft resize-none"
              />
            </Step>
          )}

          {current === "country" && (
            <Step title="Ve které zemi?">
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="addevent-country-grid">
                {COUNTRIES.map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    data-testid={`addevent-country-${c.code}`}
                    onClick={() => { set("country", c.code); setTimeout(goNext, 120); }}
                    className={[
                      "p-4 rounded-2xl bg-white border-2 text-center transition-all active:scale-95 hover:shadow-md",
                      form.country === c.code ? "border-sky-500 shadow-md" : "border-transparent hover:border-sky-200",
                    ].join(" ")}
                  >
                    <div className="text-3xl">{c.flag}</div>
                    <div className="mt-1.5 text-xs font-bold text-slate-700">{c.name}</div>
                  </button>
                ))}
              </div>
            </Step>
          )}

          {current === "country_custom" && (
            <Step title="Napiš, do jaké země se jede" helper={`Třeba: „Francie", „Indonésie" nebo „Island".`}>
              <input
                autoFocus
                data-testid="addevent-country-custom-input"
                value={form.country_custom}
                onChange={(e) => set("country_custom", e.target.value)}
                maxLength={60}
                placeholder="Francie"
                className="input-soft"
              />
            </Step>
          )}

          {current === "country_note" && (
            <Step title="Chceš k zemi něco doplnit?" helper={`Volitelné. Třeba: „Letíme přes Vídeň, kdo chce, může se přidat z Brna."`} subtleSkip>
              <textarea
                data-testid="addevent-country-note-input"
                value={form.country_note}
                onChange={(e) => set("country_note", e.target.value)}
                maxLength={120}
                rows={2}
                placeholder="(volitelné)"
                className="input-soft resize-none"
              />
              <SmallSkip onClick={goNext} testid="addevent-country-note-skip" />
            </Step>
          )}

          {current === "city" && (
            <Step title="V jakém městě?" helper={`Třeba: „Praha", „Brno" nebo „Santorini".`}>
              <input
                autoFocus
                data-testid="addevent-city-input"
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                maxLength={80}
                placeholder="Praha"
                className="input-soft"
              />
            </Step>
          )}

          {current === "region" && (
            <Step title="Který kraj?">
              <div className="grid grid-cols-2 gap-2.5 max-h-[60vh] overflow-y-auto" data-testid="addevent-region-grid">
                {REGIONS.map((r) => (
                  <button
                    key={r.code}
                    type="button"
                    data-testid={`addevent-region-${r.code}`}
                    onClick={() => { set("region", r.code); setTimeout(goNext, 120); }}
                    className={[
                      "px-4 py-3 rounded-2xl bg-white border-2 text-left transition-all active:scale-95 hover:shadow-md font-bold text-slate-700",
                      form.region === r.code ? "border-sky-500 shadow-md text-slate-900" : "border-transparent hover:border-sky-200",
                    ].join(" ")}
                  >
                    {r.name}
                  </button>
                ))}
              </div>
            </Step>
          )}

          {current === "date_text" && (
            <Step title="Kdy to bude?" helper={`Napiš to vlastními slovy. Třeba: „15:00 17.6.2026" nebo „víkend 5.–6. dubna 2026".`}>
              <input
                autoFocus
                data-testid="addevent-date-input"
                value={form.date_text}
                onChange={(e) => set("date_text", e.target.value)}
                maxLength={80}
                placeholder="15:00 17.6.2026"
                className="input-soft"
              />
              <p className="text-xs text-slate-500 font-medium mt-2.5">Pokud akci zrušíš nebo posuneš, můžeš to později upravit ve svém profilu.</p>
            </Step>
          )}

          {current === "capacity" && (
            <Step title="Pro kolik lidí?" helper={`Napiš číslo. Akce se zavře, jakmile bude plno.`}>
              <div className="flex items-center justify-center gap-4">
                <button
                  type="button"
                  data-testid="addevent-capacity-minus"
                  onClick={() => set("capacity", Math.max(2, (parseInt(form.capacity, 10) || 2) - 1))}
                  className="w-14 h-14 rounded-full bg-white border-2 border-slate-200 grid place-items-center font-extrabold text-2xl text-slate-700 hover:border-sky-300 active:scale-95"
                >
                  −
                </button>
                <input
                  type="number"
                  data-testid="addevent-capacity-input"
                  min={2} max={10000}
                  value={form.capacity}
                  onChange={(e) => set("capacity", Math.max(2, Math.min(10000, parseInt(e.target.value, 10) || 2)))}
                  className="w-32 text-center font-heading font-extrabold text-5xl text-slate-900 bg-transparent outline-none"
                />
                <button
                  type="button"
                  data-testid="addevent-capacity-plus"
                  onClick={() => set("capacity", Math.min(10000, (parseInt(form.capacity, 10) || 2) + 1))}
                  className="w-14 h-14 rounded-full bg-white border-2 border-slate-200 grid place-items-center font-extrabold text-2xl text-slate-700 hover:border-sky-300 active:scale-95"
                >
                  +
                </button>
              </div>
              <p className="text-center text-sm text-slate-500 font-medium mt-4">
                lidí
              </p>
            </Step>
          )}

          {current === "age_range" && (
            <Step title="Pro jakou věkovou skupinu?" helper={`Volitelné. Třeba: „18 – 35 let". Nech prázdné, pokud je akce pro všechny.`} subtleSkip>
              <div className="flex items-center gap-3 justify-center">
                <div className="text-center">
                  <div className="text-xs uppercase tracking-[0.12em] font-bold text-slate-400 mb-1">Od</div>
                  <input
                    type="number"
                    data-testid="addevent-age-min-input"
                    min={0} max={120}
                    value={form.age_min}
                    onChange={(e) => set("age_min", e.target.value.slice(0, 3))}
                    placeholder="—"
                    className="w-24 text-center font-heading font-extrabold text-3xl text-slate-900 bg-white rounded-2xl border-2 border-transparent focus:border-sky-400 outline-none py-3 shadow-sm"
                  />
                  <div className="text-xs text-slate-500 font-bold mt-1">let</div>
                </div>
                <div className="text-2xl text-slate-300 font-bold">–</div>
                <div className="text-center">
                  <div className="text-xs uppercase tracking-[0.12em] font-bold text-slate-400 mb-1">Do</div>
                  <input
                    type="number"
                    data-testid="addevent-age-max-input"
                    min={0} max={120}
                    value={form.age_max}
                    onChange={(e) => set("age_max", e.target.value.slice(0, 3))}
                    placeholder="—"
                    className="w-24 text-center font-heading font-extrabold text-3xl text-slate-900 bg-white rounded-2xl border-2 border-transparent focus:border-sky-400 outline-none py-3 shadow-sm"
                  />
                  <div className="text-xs text-slate-500 font-bold mt-1">let</div>
                </div>
              </div>
              <p className="text-center text-xs text-slate-500 font-medium mt-4">Bez vyplnění: akce je pro lidi všech věkových skupin.</p>
              <SmallSkip onClick={goNext} testid="addevent-age-skip" />
            </Step>
          )}

          {current === "price" && (
            <Step title="Kolik se každý složí?" helper={`Společný náklad — třeba na vstup, jídlo, benzín nebo pivo. Kdo akci vymyslel, na ní NEVYDĚLÁVÁ. Peníze jdou na samotný zážitek.`}>
              <div className="flex flex-col items-center gap-4">
                <div className="flex items-baseline gap-2">
                  <input
                    type="number"
                    data-testid="addevent-price-input"
                    min={0} max={500}
                    value={form.price_czk}
                    onChange={(e) => set("price_czk", Math.max(0, Math.min(500, parseInt(e.target.value, 10) || 0)))}
                    className="w-32 text-center font-heading font-extrabold text-5xl text-slate-900 bg-transparent outline-none"
                  />
                  <span className="font-heading font-extrabold text-2xl text-slate-500">Kč</span>
                </div>
                <span className="text-sm font-bold text-slate-500">
                  {form.price_czk === 0 ? "Zdarma" : "na osobu"}
                </span>
                <div className="grid grid-cols-5 gap-2 w-full max-w-sm">
                  {[0, 50, 100, 150, 240].map((v) => (
                    <button
                      key={v}
                      type="button"
                      data-testid={`addevent-price-quick-${v}`}
                      onClick={() => set("price_czk", v)}
                      className={[
                        "py-2 rounded-full text-xs font-bold transition-all active:scale-95",
                        form.price_czk === v
                          ? "bg-sky-500 text-white shadow-md"
                          : "bg-white border border-slate-200 text-slate-600 hover:border-sky-300",
                      ].join(" ")}
                    >
                      {v === 0 ? "Zdarma" : `${v} Kč`}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-5 rounded-xl bg-sky-50 border border-sky-100 p-3 text-xs text-sky-900 font-medium leading-relaxed">
                💡 Chtěl/a bys udělat dražší akci jako lasergame nebo skydiving?{" "}
                <b>Napiš nám</b> — ConnectYou rád zaplatí zážitek partě, kterou seženeš.
              </div>
            </Step>
          )}

          {current === "perks" && (
            <Step title="Co dostane ten, kdo přijde?" helper={`Volitelné. Třeba: „Vstupenka + 1 pivo zdarma" nebo „Letenka + hotel s bazénem".`} subtleSkip>
              <input
                data-testid="addevent-perks-input"
                value={form.perks}
                onChange={(e) => set("perks", e.target.value)}
                maxLength={200}
                placeholder="(volitelné)"
                className="input-soft"
              />
              <SmallSkip onClick={goNext} testid="addevent-perks-skip" />
            </Step>
          )}

          {current === "organizer_note" && (
            <Step title="Napiš pár vět o sobě a o akci" helper={`Ať tě parta lépe pozná. Buď upřímný/á. Třeba: „Jsem 18, jezdím do Řecka už 4 roky a chci tam vzít partu."`}>
              <textarea
                autoFocus
                data-testid="addevent-organizer-note-input"
                value={form.organizer_note}
                onChange={(e) => set("organizer_note", e.target.value)}
                maxLength={400}
                rows={4}
                placeholder="Něco o sobě a o tom, proč jsi to vymyslel/a…"
                className="input-soft resize-none"
              />
            </Step>
          )}

          {current === "confirm" && (
            <Step title="Hotovo. Mrkni na to a odešli." helper={`Pokud něco nesedí, vrať se šipkou nahoře a uprav to.`}>
              <Preview form={form} user={user} />
              <label className="mt-5 flex gap-3 items-start cursor-pointer select-none">
                <input
                  data-testid="addevent-truth-checkbox"
                  type="checkbox" checked={form.truth_consent}
                  onChange={(e) => set("truth_consent", e.target.checked)}
                  className="mt-1 w-5 h-5 accent-sky-500"
                />
                <span className="text-sm text-slate-700 font-medium">
                  Souhlasím, že vše, co jsem napsal/a, je pravda. Pokud bych lhal/a, mohu být z ConnectYou vyloučen/a.
                </span>
              </label>
              {error && (
                <div data-testid="addevent-error" className="mt-3 rounded-xl bg-red-50 text-red-700 text-sm font-semibold p-3">{error}</div>
              )}
            </Step>
          )}
        </div>
      </main>

      {/* FOOTER NAV */}
      <footer className="fixed bottom-0 left-0 right-0 backdrop-blur-md bg-white/80 border-t border-white/60">
        <div className="max-w-xl mx-auto px-4 sm:px-6 py-4 flex gap-3">
          {step > 0 && (
            <button
              data-testid="addevent-prev-btn"
              onClick={goBack}
              className="px-5 py-3 rounded-full bg-white border border-slate-200 text-slate-700 font-bold hover:border-sky-300 active:scale-95"
            >
              <ArrowLeft className="w-4 h-4 inline-block mr-1" /> Zpět
            </button>
          )}
          {current === "confirm" ? (
            <button
              data-testid="addevent-submit-btn"
              disabled={!canNext() || submitting}
              onClick={submit}
              className="flex-1 px-6 py-3 rounded-full bg-sky-500 hover:bg-sky-600 disabled:opacity-50 text-white font-extrabold transition-all active:scale-95 shadow-[0_12px_30px_-10px_rgba(14,165,233,0.55)]"
            >
              {submitting ? "Odesílám…" : "Odeslat ke schválení"}
            </button>
          ) : (
            <button
              data-testid="addevent-next-btn"
              disabled={!canNext()}
              onClick={goNext}
              className="flex-1 px-6 py-3 rounded-full bg-sky-500 hover:bg-sky-600 disabled:opacity-50 text-white font-extrabold transition-all active:scale-95 shadow-[0_12px_30px_-10px_rgba(14,165,233,0.55)]"
            >
              Pokračovat <ArrowRight className="w-4 h-4 inline-block ml-1" />
            </button>
          )}
        </div>
      </footer>

      <style>{`
        .input-soft {
          width: 100%;
          padding: 1rem 1.25rem;
          border-radius: 1rem;
          background: white;
          border: 2px solid transparent;
          font-size: 18px;
          font-weight: 600;
          color: #0f172a;
          outline: none;
          box-shadow: 0 8px 30px -12px rgba(0,0,0,0.08);
          transition: all .2s;
        }
        .input-soft:focus {
          border-color: #0EA5E9;
          box-shadow: 0 8px 30px -12px rgba(14,165,233,0.4), 0 0 0 4px rgba(14,165,233,0.15);
        }
        .input-soft::placeholder { color: #94a3b8; font-weight: 500; }
      `}</style>
    </div>
  );
}

function Step({ title, helper, children, subtleSkip }) {
  return (
    <div>
      <h1 data-testid="addevent-step-title" className="font-heading font-extrabold text-3xl sm:text-4xl text-slate-900 tracking-tight leading-tight">{title}</h1>
      {helper && <p className="text-slate-600 font-medium mt-2.5 text-[15px] leading-relaxed">{helper}</p>}
      <div className="mt-7">{children}</div>
    </div>
  );
}

function SmallSkip({ onClick, testid }) {
  return (
    <button
      data-testid={testid}
      type="button"
      onClick={onClick}
      className="mt-4 text-sm text-slate-500 hover:text-slate-700 font-bold underline underline-offset-4"
    >
      Přeskočit
    </button>
  );
}

function Preview({ form, user }) {
  const cat = CATEGORIES.find((c) => c.code === form.category);
  const country = COUNTRIES.find((c) => c.code === form.country);
  const countryDisplay = form.country === "OTHER"
    ? `🌍 ${form.country_custom}`
    : `${country?.flag} ${country?.name}`;
  const ageDisplay = (form.age_min || form.age_max)
    ? `${form.age_min || "?"}–${form.age_max || "?"} let`
    : null;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
      <div className="flex items-center gap-2">
        {cat && <div className="text-2xl">{cat.emoji}</div>}
        <span className="text-xs uppercase tracking-[0.12em] font-bold text-slate-500">{cat?.label}</span>
      </div>
      <h3 className="font-heading text-xl font-extrabold text-slate-900 mt-2">{form.title || "—"}</h3>
      <p className="text-sm text-slate-600 font-medium mt-1">{form.description}</p>
      <div className="mt-3 text-sm text-slate-600 font-medium space-y-1">
        <div className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-sky-500" /> {form.city}, {countryDisplay}</div>
        <div className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-sky-500" /> {form.date_text}</div>
        <div className="flex items-center gap-1.5"><Users className="w-4 h-4 text-sky-500" /> 0/{form.capacity} lidí{ageDisplay ? ` · věk ${ageDisplay}` : ""}</div>
        {form.perks && <div className="flex items-center gap-1.5"><Heart className="w-4 h-4 text-sky-500" /> {form.perks}</div>}
      </div>
      {form.organizer_note && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="text-xs uppercase tracking-[0.12em] font-bold text-slate-400">Kdo to vymyslel</div>
          <div className="mt-1.5 text-sm text-slate-700 font-medium leading-relaxed">{form.organizer_note}</div>
          <div className="mt-2 text-xs text-slate-500 flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> {user?.name}</div>
        </div>
      )}
    </div>
  );
}
